import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { SidebarTrigger } from './sidebar';

/**
 * True once a bar above this point has drawn the mobile sidebar trigger.
 *
 * Every page bar in the app carries one, which is right while there is one bar.
 * Bars nest, though: a resource opened as an artifact renders its own
 * `ResourcePageShell` inside the chat's, and on mobile that meant two identical
 * hamburgers a row apart, both opening the same nav — the chat's at the top of
 * the window and the artifact's below the pill row.
 *
 * Only on mobile, which is why it survived: on desktop the trigger is
 * `md:hidden` and both copies are invisible.
 */
const SidebarTriggerClaimedContext = createContext(false);

export const useSidebarTriggerClaimed = (): boolean =>
  useContext(SidebarTriggerClaimedContext);

/**
 * Marks the subtree below a bar that has drawn the trigger, so bars nested
 * inside it draw none.
 *
 * Set unconditionally by any bar that renders: either it drew the trigger
 * itself, or something above it did, and in both cases nothing below should
 * draw another.
 */
export const SidebarTriggerClaimedProvider =
  SidebarTriggerClaimedContext.Provider;

type SidebarTriggerPresence = {
  /** Called by the trigger that actually draws; returns its own removal. */
  register: () => () => void;
  /** Whether anything below the provider is currently drawing a trigger. */
  anyRendered: boolean;
};

const SidebarTriggerPresenceContext =
  createContext<SidebarTriggerPresence | null>(null);

/**
 * Reports, to the shell, whether the page below it drew a nav trigger at all.
 *
 * The claim context above answers "has someone *above me* drawn one", which is
 * what stops nested bars from doubling up. It cannot answer the question the
 * shell needs — "did anyone, anywhere in this page, draw one" — because that
 * answer travels up the tree, not down.
 *
 * It has to be asked, because on mobile the trigger is the only way into the
 * nav and a great many states never reach a page bar to draw one. A chat
 * suspends on its tools and renders a bare skeleton; a page bails to `null`
 * while a feature flag resolves; the new-trigger and share-activation pages
 * have no bar in their design at all. Each of those is a screen with no way out
 * of it — you can open a chat on a phone and be unable to get back to the
 * sidebar until the tools finish loading.
 *
 * Patching the fifteen pages that have such a state would fix today's list and
 * guarantee nothing about tomorrow's. This makes it structural: the shell draws
 * the trigger whenever the page did not, so "the nav is reachable" stops being
 * a thing each page has to remember.
 */
export const SidebarTriggerPresenceProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [count, setCount] = useState(0);
  const register = useCallback(() => {
    setCount((previous) => previous + 1);
    return () => setCount((previous) => previous - 1);
  }, []);
  const value = useMemo<SidebarTriggerPresence>(
    () => ({ register, anyRendered: count > 0 }),
    [register, count]
  );
  return (
    <SidebarTriggerPresenceContext.Provider value={value}>
      {children}
    </SidebarTriggerPresenceContext.Provider>
  );
};

/**
 * Whether the page drew its own nav trigger.
 *
 * `true` outside a provider, so a surface that never opted into this — the v0
 * app, a component under test — is left exactly as it was rather than growing a
 * nav bar it never had.
 */
export const usePageDrewSidebarTrigger = (): boolean =>
  useContext(SidebarTriggerPresenceContext)?.anyRendered ?? true;

/**
 * The mobile nav strip a page draws above its content when it has no bar of its
 * own to put the trigger in.
 *
 * One definition rather than one per page: the strip is a fixed piece of the
 * app's chrome — pages that grow one should be indistinguishable from pages
 * that always had one, and its height is load-bearing (the shell reserves the
 * remaining height for the route below it).
 *
 * `[&:empty]:hidden` because the usual child is {@link PageBarSidebarTrigger},
 * which renders nothing where an ancestor bar has already claimed the trigger —
 * without it those pages kept a 44px band of empty sidebar colour.
 */
export const PageNavStrip: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="flex h-11 shrink-0 items-center bg-sidebar px-4 md:hidden [&:empty]:hidden">
    {children}
  </div>
);

/**
 * The mobile nav trigger for a page bar, drawn only by the outermost bar that
 * wants one. Renders nothing where an ancestor bar has already claimed it.
 */
export const PageBarSidebarTrigger: React.FC = () => {
  const claimed = useSidebarTriggerClaimed();
  const presence = useContext(SidebarTriggerPresenceContext);
  const register = presence?.register;

  // `useLayoutEffect`, not `useEffect`: the shell's fallback reads the count
  // this keeps, and a passive effect lands after paint — so every page that
  // does draw its own bar would show the fallback for one frame first, and the
  // page would visibly jump down by the fallback's height and back.
  useLayoutEffect(() => {
    if (claimed || register === undefined) {
      return;
    }
    return register();
  }, [claimed, register]);

  if (claimed) {
    return null;
  }
  return (
    <div className="flex items-center md:hidden">
      <SidebarTrigger />
    </div>
  );
};

/**
 * The nav trigger for a page that drew none — see
 * {@link SidebarTriggerPresenceProvider}.
 *
 * A bar rather than a button floating over the content: a page with no bar
 * starts its content at the top of the window, and an overlaid hamburger would
 * sit on top of whatever that content's first line is. The strip matches the
 * one `ListPageShell` draws for the same purpose on a top-level list, so the
 * pages that grow one here look like the pages that always had one.
 */
export const FallbackPageSidebarTrigger: React.FC = () => {
  if (usePageDrewSidebarTrigger()) {
    return null;
  }
  return (
    <PageNavStrip>
      <SidebarTrigger />
    </PageNavStrip>
  );
};
